import Quill from 'quill';
import "quill-mention/autoregister";

const BlockEmbed = Quill.import('blots/block/embed') as any;
const Block = Quill.import('blots/block') as any;
const Inline = Quill.import('blots/inline') as any;
const MentionBlot = Quill.import('blots/mention') as any;

export class FrozenTableBlot extends BlockEmbed {
    static blotName = 'frozen-table';
    static tagName = 'TABLE';
    static className = 'ql-frozen-table';

    static create(value: string): HTMLElement {
        const node = super.create() as HTMLElement;
        node.setAttribute('contenteditable', 'false');
        const temp = document.createElement('div');
        temp.innerHTML = value || '';
        const parsedTable = temp.querySelector('table');

        if (parsedTable) {
            Array.from(parsedTable.attributes).forEach(attr => {
                if (attr.name === 'class') {
                    attr.value.split(/\s+/).filter(Boolean).forEach(name => node.classList.add(name));
                } else if (attr.name !== 'contenteditable') {
                    node.setAttribute(attr.name, attr.value);
                }
            });
            node.innerHTML = parsedTable.innerHTML;
        } else {
            node.innerHTML = value || '';
        }

        return node;
    }

    static value(node: HTMLElement): string {
        const clone = node.cloneNode(true) as HTMLElement;
        clone.classList.remove(this.className);
        if (clone.classList.length === 0) clone.removeAttribute('class');
        clone.removeAttribute('contenteditable');
        return clone.outerHTML;
    }
}

export class EditableFrozenTableBlot extends FrozenTableBlot {
    static override blotName = 'editable-frozen-table';
    static override className = 'ql-editable-frozen-table';

    static override create(value: string): HTMLElement {
        const node = super.create(value) as HTMLElement;
        node.querySelectorAll('td, th').forEach(cell => cell.setAttribute('contenteditable', 'true'));
        return node;
    }
}

// Custom block blot that preserves style attribute
export class StyledBlockBlot extends Block {
    static blotName = 'styled-block';
    static tagName = 'P';

    static create(value: any): HTMLElement {
        const node = super.create() as HTMLElement;
        if (value && typeof value === 'string') {
            node.setAttribute('style', value);
        }
        return node;
    }

    static formats(node: HTMLElement): any {
        return node.getAttribute('style') || undefined;
    }

    format(name: string, value: any): void {
        if (name === 'styled-block') {
            if (value) {
                this['domNode'].setAttribute('style', value);
            } else {
                this['domNode'].removeAttribute('style');
            }
        } else {
            try {
                super.format(name, value);
            } catch {
                // Quill 2.x Block.format() may fail for certain format types
            }
        }
    }
}

// Custom inline blot that preserves style attribute
export class StyledInlineBlot extends Inline {
    static blotName = 'styled-inline';
    static tagName = 'SPAN';

    static create(value: string): HTMLElement {
        const node = super.create() as HTMLElement;
        if (value) {
            node.setAttribute('style', value);
        }
        return node;
    }

    static formats(node: HTMLElement): string | null {
        return node.getAttribute('style');
    }

    format(name: string, value: string): void {
        if (name === 'styled-inline') {
            if (value) {
                this['domNode'].setAttribute('style', value);
            } else {
                this['domNode'].removeAttribute('style');
            }
        } else {
            try {
                super.format(name, value);
            } catch {
                // Quill 2.x Inline.format() may fail for certain format types
            }
        }
    }
}

export function wrapTablesForQuill(html: string, editable: boolean = false): string {
    if (!html) {
        return '';
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const markerClass = editable ? 'ql-editable-frozen-table' : 'ql-frozen-table';
    tempDiv.querySelectorAll('table').forEach((table) => {
        if (table.classList.contains('ql-table-better') || table.classList.contains('nai-editable-table')) {
            return;
        }
        table.classList.add(markerClass);
        table.setAttribute('contenteditable', 'false');
    });

    return tempDiv.innerHTML;
}

export function unwrapFrozenTables(html: string): string {
    if (!html) {
        return '';
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('table.ql-frozen-table, table.ql-editable-frozen-table').forEach((table) => {
        table.classList.remove('ql-frozen-table', 'ql-editable-frozen-table');
        table.removeAttribute('contenteditable');
        table.querySelectorAll('td, th').forEach(cell => cell.removeAttribute('contenteditable'));
        if (table.classList.length === 0) {
            table.removeAttribute('class');
        }
    });

    return tempDiv.innerHTML;
}

export function sanitizeTableBetterHtml(html: string): string {
    if (!html || !html.includes('ql-table-better')) {
        return html;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    tempDiv.querySelectorAll('temporary').forEach(el => el.remove());

    // Only sanitize tables created/managed by quill-table-better (identified by the ql-table-better class)
    const tables = tempDiv.querySelectorAll('table.ql-table-better');
    tables.forEach(table => {

        // Remove all attributes from table and its structural children (thead, tbody, tr, th, td)
        const structuralElements = [table, ...Array.from(table.querySelectorAll('thead, tbody, tr, th, td'))];
        structuralElements.forEach(el => {
            while (el.attributes.length > 0) {
                el.removeAttribute(el.attributes[0].name);
            }
        });

        // Remove all attributes from elements inside th and td cells
        table.querySelectorAll('th *, td *').forEach(el => {
            while (el.attributes.length > 0) {
                el.removeAttribute(el.attributes[0].name);
            }
        });

        table.setAttribute('class', 'nai-editable-table');
    });

    return tempDiv.innerHTML;
}

class CustomMentionBlot extends MentionBlot {
    static create(data: any) {
        const node = super.create(data);
        if (data['variableId']) {
            node.setAttribute('data-variable-id', data['variableId']);
        }
        return node;
    }
}

export function sanitizeQuillOutput(html: string): string {
    if (!html) return html;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    cleanNode(doc.body);

    let result = doc.body.innerHTML;

    // Restore self-closing slash on img tags (browser DOM strips it)
    result = result.replace(/<img\b([^>]*?)(\s*\/)?>/gi, '<img$1 />');

    return result;
}

function cleanNode(node: Element): void {
    if (node.classList) {
        const qlClasses = Array.from(node.classList).filter(c => c.startsWith('ql-'));
        qlClasses.forEach(c => node.classList.remove(c));
        if (node.classList.length === 0) {
            node.removeAttribute('class');
        }
    }

    Array.from(node.attributes)
        .filter(attr => attr.name === 'contenteditable')
        .forEach(attr => node.removeAttribute(attr.name));

    Array.from(node.children).forEach(child => cleanNode(child as Element));
}

export function registerQuillBlots(): void {
    Quill.register(FrozenTableBlot, true);
    Quill.register('formats/editable-frozen-table', EditableFrozenTableBlot, true);
    Quill.register(StyledBlockBlot, true);
    Quill.register(StyledInlineBlot, true);
    Quill.register(CustomMentionBlot, true);

    const AlignStyle = Quill.import('attributors/style/align') as any;
    const DirectionStyle = Quill.import('attributors/style/direction') as any;
    const FontStyle = Quill.import('attributors/style/font') as any;
    const SizeStyle = Quill.import('attributors/style/size') as any;
    const ColorStyle = Quill.import('attributors/style/color') as any;
    const BackgroundStyle = Quill.import('attributors/style/background') as any;

    FontStyle.whitelist = null;
    SizeStyle.whitelist = null;
    ColorStyle.whitelist = null;
    BackgroundStyle.whitelist = null;

    Quill.register(AlignStyle, true);
    Quill.register(DirectionStyle, true);
    Quill.register(FontStyle, true);
    Quill.register(SizeStyle, true);
    Quill.register(ColorStyle, true);
    Quill.register(BackgroundStyle, true);
}
